import { useFormikSubmit } from "@certn/api";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import {
  Container,
  Divider,
  SelectFormikField,
  SelectItem,
  Text,
  ProgressLoader,
} from "@certn/cascadia-react";
import { Stack } from "@certn/cascadia";
import { getRegionKey } from "@certn/utils";
import { css } from "@emotion/css";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { components } from "@certn/api/generated-schemas/client-portal";
import { ReportUrlTypeField } from "../../../components/ReportUrlTypeField";
import { WorkableIntegrationStepLayout } from "../WorkableIntegrationStepLayout";
import {
  AtsIntegration,
  WorkableIntegrationContext,
} from "../../WorkableIntegrationContext";

export function WorkableSettingsStep() {
  const { t } = useTranslation();
  const context = useContext(WorkableIntegrationContext);
  const {
    atsIntegrationData: contextData,
    setAtsIntegration,
    goToNextStep,
  } = context || {
    atsIntegrationData: null,
    setAtsIntegration: () => {},
    goToNextStep: () => {},
  };

  // AU (APAC) only permits the authenticated Client Portal URL for Workable.
  const isAuRegion = getRegionKey() === "au";

  const integrationData = contextData || {
    integration_id: "",
    api_key: "",
    report_url_type: "CLIENT_PORTAL_URL",
    customer_group_id: "",
  };

  // Seed the restricted value directly so the disabled PDF option is never the
  // initial selection (avoids a one-frame flicker and a dirtied read-only form).
  const initialFormValues = isAuRegion
    ? { ...integrationData, report_url_type: "CLIENT_PORTAL_URL" as const }
    : integrationData;

  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );

  const validationSchema = object().shape({
    customer_group_id: string().required().label(t("Default group")),
  });

  const { submit: createIntegration, isSubmitting: isCreating } =
    useFormikSubmit("/api/internal/client-portal/ats-integrations/create/", {
      onSuccess: ({ id, api_key }: { id: string; api_key: string }) => {
        setAtsIntegration((prev) => ({
          ...prev,
          integration_id: id,
          api_key,
        }));
        goToNextStep();
      },
      showToastOnError: true,
    });

  const updateUrl = integrationData.integration_id
    ? `/api/internal/client-portal/ats-integrations/${integrationData.integration_id}/update/`
    : "";

  const { submit: updateIntegration, isSubmitting: isUpdating } =
    useFormikSubmit<components["schemas"]["AtsIntegrationDetail"]>(updateUrl, {
      onSuccess: () => {
        goToNextStep();
      },
      showToastOnError: true,
    });

  const isSubmitting = isCreating || isUpdating;

  const handleSubmit = useCallback(
    async (
      values: AtsIntegration,
      formikHelpers: FormikHelpers<AtsIntegration>
    ) => {
      setAtsIntegration(values);

      const payload = {
        report_url_type: values.report_url_type || "CLIENT_PORTAL_URL",
        lever_integration: null,
        workday_integration: null,
        workable_integration: {
          customer_group_id: values.customer_group_id || "",
        },
      };

      if (integrationData.integration_id) {
        await updateIntegration(
          payload as unknown as components["schemas"]["AtsIntegrationDetail"],
          formikHelpers
        );
      } else {
        await createIntegration(payload, formikHelpers);
      }
    },
    [
      setAtsIntegration,
      createIntegration,
      updateIntegration,
      integrationData.integration_id,
    ]
  );

  const shouldDisableForm = isSubmitting;

  return (
    <>
      <Formik
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ handleSubmit, isValid }) => {
          const formFields = (
            <>
              <ReportUrlTypeField
                isDisabled={shouldDisableForm}
                defaultValue="CLIENT_PORTAL_URL"
                isPdfUrlDisabled={isAuRegion}
                note={
                  isAuRegion
                    ? t(
                        "Only the Client Portal URL (Authenticated) option is available in the APAC region."
                      )
                    : undefined
                }
              />

              <Divider />

              <section>
                <SelectFormikField
                  label={t("Select a group")}
                  testID="workable-customer-group"
                  name="customer_group_id"
                  isRequired
                  isLoading={isLoadingCustomerGroups}
                  isDisabled={shouldDisableForm}
                >
                  {customerGroups?.map(({ id, name }) => (
                    <SelectItem key={id} id={id} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
              </section>
            </>
          );

          return (
            <WorkableIntegrationStepLayout
              onComplete={handleSubmit}
              disableCompletion={!isValid}
              submitting={isSubmitting}
            >
              <Container
                layout="narrow"
                testID="workable-setup-page-outermost-container"
                className={styles.container}
              >
                <Form>
                  <Stack gap="space-3">
                    <div>
                      <Text style="input/section-title">
                        {t("step {{stepNumber}} of {{stepCount}}", {
                          stepNumber: 1,
                          stepCount: 2,
                        })}
                      </Text>
                      <Text
                        as="h1"
                        className={styles.titleSpacing}
                        style="global/title-small"
                      >
                        {t("{{integration}} integration", {
                          integration: "Workable",
                        })}
                      </Text>
                    </div>
                    <ProgressLoader floating value={50} />
                  </Stack>
                  <div className={styles.mainContentContainer}>
                    {formFields}
                  </div>
                </Form>
              </Container>
            </WorkableIntegrationStepLayout>
          );
        }}
      </Formik>
    </>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
};
